/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { FileRequestAction } from '@moderepo/mode-apis';
import { Project } from '@moderepo/mode-internal-apis';
import { EntitiesListingViewType } from '../components';
import { isEntityClassUISettings } from '../containers';
import { CustomDrawerMenuConfig, DeviceRegistrationWizardSettings, EntitiesFeatureSettings, EntitiesListingViewSettings,
    EntitiesTableViewAttributesSettings, EntitiesTableViewSettings, EntitiesViewsSettings, GridContainerConfig, GridItemConfig,
    ProjectCustomUIConfig, RitzComponentConfig, RitzDrawerMainMenuItem, RitzDrawerMenuItem, RitzFeaturesConfig, RitzPageConfig }
    from './ritzUIExtensionUtils';
import { isValidSettingStringValue, isObjectLiteral, isValidSettingBooleanValue } from './utils';
import { TableViewHiddenFields } from '../containers/entities/EntitiesListingContainer';
import { SettingsErrorLogger } from './logger';


/**
 * @param config A type guard function to check in a config is a fileTransfer feature config.
 * A config is a fileTransfer feature config if it contain 'supportedActions' object and in that object there is settings for at least one
 * of the possible 4 actions, download from device, download from robot, upload to device, or upload to robot.
 * @returns
 */
const isFileTransferFeatureConfig = (config: unknown): config is RitzFeaturesConfig['fileTransfer'] => {
    const item = config as RitzFeaturesConfig['fileTransfer'];
    return (item !== undefined && typeof item.supportedActions === 'object')
        && (
            item.supportedActions[FileRequestAction.DOWNLOAD_FROM_DEVICE] === undefined
            || typeof item.supportedActions[FileRequestAction.DOWNLOAD_FROM_DEVICE] === 'object')
        && (
            item.supportedActions[FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER] === undefined
            || typeof item.supportedActions[FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER] === 'object')
        && (
            item.supportedActions[FileRequestAction.DOWNLOAD_FROM_ROBOT] === undefined
            || typeof item.supportedActions[FileRequestAction.DOWNLOAD_FROM_ROBOT] === 'object')
        && (
            item.supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] === undefined
            || typeof item.supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] === 'object')
        && (
            item.supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] === undefined
            || typeof item.supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] === 'object')
        && (
            item.supportedActions[FileRequestAction.UPLOAD_TO_CONTROL_SERVER] === undefined
            || typeof item.supportedActions[FileRequestAction.UPLOAD_TO_CONTROL_SERVER] === 'object')
        && (
            item.supportedActions[FileRequestAction.UPLOAD_TO_ROBOT] === undefined
            || typeof item.supportedActions[FileRequestAction.UPLOAD_TO_ROBOT] === 'object')
        && (Object.keys(item.supportedActions).length > 0);
};


/**
 * Type guard function to check if the 'config' param is a 'deviceRegistrationWizard' settings object
 */
const isDeviceRegistrationWizardFeatureSettings = (config: unknown): config is DeviceRegistrationWizardSettings => {
    const deviceRegistrationWizard = config as DeviceRegistrationWizardSettings;
    return (deviceRegistrationWizard !== undefined && isObjectLiteral(deviceRegistrationWizard))
        && (deviceRegistrationWizard.deviceClassesSettings instanceof Array)
        && (deviceRegistrationWizard.deviceClassesSettings.every((settings) => {
            // Each deviceClassSettings must have the settings for all these attributes
            return isValidSettingStringValue(settings.deviceClass, true, false)
                    && isValidSettingStringValue(settings.entityClass, true, false)
                    && isObjectLiteral(settings.deviceConfigTemplate)
                    && isValidSettingStringValue(settings.deviceConfigFileName, false, false);
        }))
        && isValidSettingBooleanValue(deviceRegistrationWizard.allowAddDeviceWithoutEntity);
};


/**
 * Type guard function to check if the 'object' param is a 'EntitiesTableViewAttributesSettings' object
 */
const isEntitiesTableViewAttributesSettings = (object: unknown): object is EntitiesTableViewAttributesSettings => {
    const settings = object as EntitiesTableViewAttributesSettings;
    type key = keyof TableViewHiddenFields;
    return (settings !== undefined && isObjectLiteral(settings))
        && (settings.includeAttributes === undefined || (settings.includeAttributes instanceof Array && settings.includeAttributes.every((value) => {
            return isValidSettingStringValue(value, true);
        })))
        && (settings.excludeAttributes === undefined || (settings.excludeAttributes instanceof Array && settings.excludeAttributes.every((value) => {
            return isValidSettingStringValue(value, true);
        })));
};



/**
 * Type guard function to check if the 'object' param is a 'EntitiesTableViewSettings' object
 */
const isEntitiesTableViewSettings = (object: unknown): object is EntitiesTableViewSettings => {
    const settings = object as EntitiesTableViewSettings;
    type key = keyof TableViewHiddenFields;
    return (settings !== undefined && isObjectLiteral(settings))
        && (settings.hiddenFields === undefined || (isObjectLiteral(settings.hiddenFields) && Object.values(settings.hiddenFields).every((value) => {
            return typeof value === 'boolean';
        })))
        && (settings.fieldsOrder === undefined || (settings.fieldsOrder instanceof Array && settings.fieldsOrder.every((value) => {
            return isValidSettingStringValue(value, true);
        })))
        && (settings.showAttributesSettings === undefined || isEntitiesTableViewAttributesSettings(settings.showAttributesSettings));
};



/**
 * Type guard function to check if the 'object' param is a 'EntitiesListingViewSettings' object
 */
const isEntitiesListingViewSettings = (object: unknown): object is EntitiesListingViewSettings => {
    const settings = object as EntitiesListingViewSettings;
    return (settings !== undefined && isObjectLiteral(settings))
        && (isValidSettingStringValue(settings.defaultViewType, false, false, Object.values(EntitiesListingViewType)))
        && (isValidSettingBooleanValue(settings.enableChangeViewOptions))
        && (isValidSettingBooleanValue(settings.enableSearchOptions))
        && (settings.tableView === undefined || isEntitiesTableViewSettings(settings.tableView))
        && (settings.tableViewPerEntityClasses === undefined
            || (settings.tableViewPerEntityClasses instanceof Array && settings.tableViewPerEntityClasses.every((entityClassesSettings) => {
                return isObjectLiteral(entityClassesSettings)
                    // Make sure entityClasses is an array of non-empty strings and has at least 1 entityClass.
                    && entityClassesSettings.entityClasses instanceof Array && entityClassesSettings.entityClasses.length > 0
                    && entityClassesSettings.entityClasses.every((entityClass) => {
                        return isValidSettingStringValue(entityClass, true, false);
                    })
                    && isEntitiesTableViewSettings(entityClassesSettings.settings);
            })));
};



/**
 * Type guard function to check if the 'object' param is a 'EntitiesViewSettings' object
 */
const isEntitiesViewsSettings = (object: unknown): object is EntitiesViewsSettings => {
    const settings = object as EntitiesViewsSettings;
    return (settings !== undefined && isObjectLiteral(settings))
        && (settings.manageView === undefined || isEntitiesListingViewSettings(settings.manageView))
        && (settings.dataView === undefined || isEntitiesListingViewSettings(settings.dataView));
};


/**
 * Type guard function to check if the 'object' param is a 'EntitiesFeatureSettings' object
 */
const isEntitiesFeatureSettings = (object: unknown): object is EntitiesFeatureSettings => {
    const settings = object as EntitiesFeatureSettings;
    return (settings !== undefined && isObjectLiteral(settings))
        && (settings.viewsSettings === undefined || isEntitiesViewsSettings(settings.viewsSettings));
};



/**
 * This is a type guard function to check if a config object is a RitzFeaturesConfig.
 * An object is a RitzFeaturesConfig if it has config for one of the features e.g. `fileTransfer`.
 */
const isRitzFeaturesConfig = (config: unknown): config is RitzFeaturesConfig => {
    const item = config as RitzFeaturesConfig;

    // A config object is a RitzFeaturesConfig if it has value for all the required props and the values are correct type
    return (item.fileTransfer === undefined || isFileTransferFeatureConfig(item.fileTransfer))
        && (item.deviceRegistrationWizard === undefined || isDeviceRegistrationWizardFeatureSettings(item.deviceRegistrationWizard))
        && (item.tsdbTabVisible === undefined || typeof item.tsdbTabVisible === 'boolean')
        && (item.videosTabVisible === undefined || typeof item.videosTabVisible === 'boolean')
        && (item.entities === undefined || typeof item.entities === 'boolean' || isEntitiesFeatureSettings(item.entities))
        && (item.alerting === undefined || typeof item.alerting === 'boolean')
        && (item.disableUserlessHome === undefined || typeof item.disableUserlessHome === 'boolean');
};



/**
 * Type guard to check if a config object is a RitzDrawerMenuItem.
 */
const isRitzDrawerMenuItem = (config: unknown): config is RitzDrawerMenuItem => {
    const item = config as RitzDrawerMenuItem;

    // A config object is a RitzDrawerMenuItem if it has value for all the required props and the values are correct type
    return (!item.itemName || (typeof item.itemName === 'string' && item.itemName.length > 0))
        && (typeof item.labelKey === 'string' && item.labelKey.length > 0)
        && (!item.iconKey || (typeof item.iconKey === 'string' && item.iconKey.length > 0))
        && (!item.link || (typeof item.link === 'string' && item.link.length > 0))  // These 4 are OPTIONAL so they can be undefined
        && (item.disabled === undefined || typeof item.disabled === 'boolean')      // BUT if they are not undefined, they must be the correct type
        && (item.hidden === undefined || typeof item.hidden === 'boolean')
        && (item.adminOnly === undefined || typeof item.adminOnly === 'boolean');
};



/**
 * Type guard to check if a config object is a Partial<RitzDrawerMenuItem>. This is similar to the isRitzDrawerMenuItem function except that
 * the props are NOT required
 */
const isPartialRitzDrawerMenuItem = (config: unknown): config is RitzDrawerMenuItem => {
    const item = config as RitzDrawerMenuItem;

    // A config object is a RitzDrawerMenuItem if it DOES NOT have a value for the prop but if it does, the values MUST be valid
    return (!item.itemName || (typeof item.itemName === 'string' && item.itemName.length > 0))
        && (!item.labelKey || (typeof item.labelKey === 'string' && item.labelKey.length > 0))
        && (!item.iconKey || (typeof item.iconKey === 'string' && item.iconKey.length > 0))
        && (!item.link || (typeof item.link === 'string' && item.link.length > 0))
        && (item.disabled === undefined || (typeof item.disabled === 'boolean'))
        && (item.hidden === undefined || (typeof item.hidden === 'boolean'))
        && (item.adminOnly === undefined || (typeof item.adminOnly === 'boolean'));
};


/**
 * Type guard to check if the given config is a type of RitzDrawerMainMenuItem
 */
export const isRitzDrawerMainMenuItem = (config: unknown): config is RitzDrawerMainMenuItem => {
    const item = config as RitzDrawerMainMenuItem;

    // A config is a RitzDrawerMainMenuItem if it has all the props set correctly
    return (!item.subMenuItems || (item.subMenuItems instanceof Array && !item.subMenuItems.find((subItem) => {
        return !isRitzDrawerMenuItem(subItem);
    }))) && isRitzDrawerMenuItem(item);
};



/**
 * Type guard to check if the given config is a type of Partial<RitzDrawerMainMenuItem>. This is similar to the isRitzDrawerMainMenuItem
 * except that the props are NOT required
 */
const isPartialRitzDrawerMainMenuItem = (config: unknown): config is RitzDrawerMainMenuItem => {
    const item = config as RitzDrawerMainMenuItem;

    // A config is a Partial<RitzDrawerMainMenuItem> if it DOES not have subMenuItems settings. But if subMenuItems is set,
    // ALL the items in the subMenuItems must be of type Partial<RitzDrawerMenuItem>
    // ALso, item itself MUST also be a type of Partial<RitzDrawerMainMenuItem>
    return (!item.subMenuItems || (item.subMenuItems instanceof Array && !item.subMenuItems.find((subItem) => {
        return !isPartialRitzDrawerMenuItem(subItem);
    }))) && isPartialRitzDrawerMenuItem(item);
};



/**
 * Type guard to check if the provided config is a valid CustomDrawerMenuConfig
 */
const isCustomDrawerMenuConfig = (config: unknown): config is CustomDrawerMenuConfig => {
    const menuConfig = config as CustomDrawerMenuConfig;

    // A config is a CustomDrawerMenuConfig if it does not have any prop set. But if a prop is set, the value of the prop MUST be valid type
    return ((!menuConfig.appNameKey || typeof menuConfig.appNameKey === 'string')
        && (!menuConfig.backgroundImage || typeof menuConfig.backgroundImage === 'string')
        && (!menuConfig.menuItems || (menuConfig.menuItems instanceof Array && !menuConfig.menuItems.find(((item) => {
            // find an item that is NOT valid RitzDrawerMainMenuItem
            return !isPartialRitzDrawerMainMenuItem(item);
        })))))
        && (menuConfig.expandAllOnLoad === undefined || typeof menuConfig.expandAllOnLoad === 'boolean')
        && (menuConfig.collapsible === undefined || typeof menuConfig.collapsible === 'boolean');
};


/**
 * Type guard to check if a config is a Ritz component config
 */
export const isRitzComponentConfig = (config: unknown): config is RitzComponentConfig => {
    const actualConfig = config as RitzComponentConfig;

    // A config is a RitzComponentConfig if it has a componentName
    return (actualConfig.componentName !== undefined)
        && (typeof actualConfig.routeParamProps === 'object')
        && (!actualConfig.compProps || typeof actualConfig.compProps === 'object');
};


/**
 * Type guard to check if a config is a Grid Item config
 */
export const isGridItemConfig = (config: unknown): config is GridItemConfig => {
    const actualConfig = config as GridItemConfig;
    return (actualConfig.components !== undefined)
        && (!actualConfig.compProps || typeof actualConfig.compProps === 'object');
};


/**
 * Type guard to check if a config is a Grid Container config
 */
export const isGridContainerConfig = (config: unknown): config is GridContainerConfig => {
    const actualConfig = config as GridContainerConfig;

    return (actualConfig.gridItems !== undefined)
        && (actualConfig.gridItems instanceof Array && !actualConfig.gridItems.find((item) => {
            // Find a BAD item config and return true if we find one
            return !isGridItemConfig(item);
        }))
        && (!actualConfig.compProps || typeof actualConfig.compProps === 'object');
};



/**
 * Type guard to check if the provided config is a valid RitzPageConfig
 */
const isRitzPageConfig = (config: unknown): config is RitzPageConfig => {
    const actualConfig = config as RitzPageConfig;
    return (!actualConfig.titleKey || (typeof actualConfig.titleKey === 'string' && actualConfig.titleKey.length > 0))
        && (!actualConfig.subtitleKey || (typeof actualConfig.subtitleKey === 'string' && actualConfig.subtitleKey.length > 0))
        && (!actualConfig.components || (actualConfig.components instanceof Array && !actualConfig.components.find((comp) => {
            // Find a config that is INVALID and return true if we can find one.
            return !isRitzComponentConfig(comp) && !isGridContainerConfig(comp);
        })));
};



/**
 * Type guard to check if a given config is a ProjectCustomUIConfig
 */
export const isProjectCustomUIConfig = (
    config: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): config is ProjectCustomUIConfig => {
    const customConfig = config as ProjectCustomUIConfig;
    const configPath = [...(currentConfigPath ?? [])];

    // config is a ProjectCustomUIConfig if it doesn't have any settings but if it has settings e.g. drawerMenu/projectDashboardPage,
    // the settings MUST be valid CustomDrawerMenuConfig or CustomRitzPageConfig
    return (!customConfig.drawerMenu || isCustomDrawerMenuConfig(customConfig.drawerMenu))
        && (customConfig.landingPage === undefined || isValidSettingStringValue(customConfig.landingPage))
        && (!customConfig.projectDashboardPage || isRitzPageConfig(customConfig.projectDashboardPage))
        && (!customConfig.enabledFeatures || isRitzFeaturesConfig(customConfig.enabledFeatures))
        && (!customConfig.entityClassUISettings || isEntityClassUISettings(
            customConfig.entityClassUISettings, errorLogger, [...configPath, 'entityClassUISettings'])
        );
};



/**
 * This function getSpecificProjectCustomUIConfig get called many time. We don't want to keep validating the project.supportToolSettings.uiSettings
 * every time so we will keep track of which uiSettings we already validated and if this function get called again and if we have already validated
 * the uiSettings object then just return the uiSettings object.
 */
const validatedProjectsConfig = new Map<number, Object>();



/**
 * Get the project.supportToolSettings.uiSettings object and return the object if it is a VALID config object. If it is NOT valid, return
 * undefined even if it is configured. We need to make sure the object has the correct settings before we use it or else it can break the UI.
 */
export const getSpecificProjectCustomUIConfig = (project: Project): ProjectCustomUIConfig | undefined => {

    // Get the latest uiSettings from the project.supportToolSettings
    const { uiSettings } = project.supportToolSettings;

    if (uiSettings) {
        // Now check it uiSettings is valid. But first, see if we have already validated this object. If we did then we can return it
        // without validating it again. We need to compare the object by reference to make sure it is the SAME object we validated and cached
        if (validatedProjectsConfig.get(project.id) === uiSettings) {
            // this uiSettings object already existed in the validatedProjectsConfig so it is good, we can return it
            return uiSettings;
        }

        // If uiSettings does not exist in the validatedProjectsConfig, validate it now
        try {
            const errorLogger = new SettingsErrorLogger('"Project UI Settings"', uiSettings);
            if (isProjectCustomUIConfig(uiSettings, errorLogger, [])) {
                // If the uiSettings is VALID, add it to the validatedProjectsConfig so we remember that it is validated and return it
                validatedProjectsConfig.set(project.id, uiSettings);

                return uiSettings;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Invalid UI Settings', error);
        }
    }

    return undefined;
};
